import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { liveQuery } from 'dexie';

import { navigate } from '../../app/hashRoute';
import { conversationRepo, messageRepo, presetRepo } from '../../core/repositories';
import { loadModelSettings } from '../../core/settings';
import type { ChatMessage, Conversation, Preset } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { ComponentMetaInput } from '../../ui';
import { setActiveConversationId } from '../chat/activeConversation';
import { loadModelPreference } from '../chat/modelPreference';
import { buildContactThreads, unreadLabel } from './threads';
import type { ContactThread } from './threads';
import styles from './messages.module.css';

const PAGE_TOP = 24;

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  if (date.toDateString() === now.toDateString()) return `${hh}:${mm}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return '昨天';
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

function preview(message: ChatMessage | null): string {
  if (message === null) return '还没有消息';
  if (message.images && message.images.length > 0) return '[图片]';
  if (message.audioUrl) return '[语音]';
  if (message.attachments && message.attachments.length > 0) return '[附件]';
  return message.content.replace(/\s+/g, ' ').trim().slice(0, 44) || '还没有消息';
}

function avatarSource(value: string | undefined): string | null {
  if (!value) return null;
  return /^(data:image\/|https?:\/\/|blob:)/i.test(value) ? value : null;
}

function firstText(...values: (string | undefined)[]): string {
  return values.find((value) => value !== undefined && value.trim() !== '')?.trim() ?? '';
}

function Avatar({ thread }: { thread: ContactThread }): ReactElement {
  const source = avatarSource(thread.avatar);
  return (
    <span className={styles.avatar} aria-hidden="true">
      {source ? <img src={source} alt="" /> : (firstText(thread.avatar, thread.title.charAt(0), '聊'))}
    </span>
  );
}

interface RowProps {
  thread: ContactThread;
  onOpen: (thread: ContactThread) => void;
}

function ThreadRow({ thread, onOpen }: RowProps): ReactElement {
  const rowMeta: ComponentMetaInput = {
    id: `messages.thread.${thread.key}`,
    page: 'messages',
    role: 'button',
    label: `消息：${thread.title}`,
    layout: { x: 12, y: PAGE_TOP + 74, width: 366, height: 72 },
    zIndex: 4,
    props: ['title', 'unreadCount'],
    notes: '联系人消息行；安卓用 RecyclerView item',
  };
  const ref = useComponentMeta<HTMLDivElement>(buildComponentMeta(rowMeta, 'container'));
  const badge = unreadLabel(thread.unreadCount);
  return (
    <motion.li layout className={styles.rowWrap}>
      <div
        ref={ref}
        className={styles.row}
        data-ui-id={rowMeta.id}
        data-ui-role="button"
        data-skin-surface="panel"
      >
        <button type="button" className={styles.rowMain} aria-label={`消息：${thread.title}`} onClick={() => onOpen(thread)}>
          <span className={styles.avatarWrap}>
            <Avatar thread={thread} />
            {badge ? <span className={styles.badge} aria-label={`${thread.unreadCount} 条未读消息`}>{badge}</span> : null}
          </span>
          <span className={styles.copy}>
            <span className={styles.nameLine}>
              <strong>{thread.title}</strong>
              <time>{formatTime(thread.lastActivityAt)}</time>
            </span>
            <span className={styles.metaLine}>
              <span className={styles.preview}>{preview(thread.lastMessage)}</span>
            </span>
          </span>
        </button>
      </div>
    </motion.li>
  );
}

function ContactPicker({
  presets,
  onClose,
  onChoose,
}: {
  presets: Preset[];
  onClose: () => void;
  onChoose: (preset: Preset) => void;
}): ReactElement {
  const presetsMotion = useMotionPresets();
  return (
    <>
      <motion.div
        className={styles.backdrop}
        variants={presetsMotion.sheetBackdrop}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={onClose}
      />
      <motion.section
        className={styles.picker}
        role="dialog"
        aria-modal="true"
        aria-label="选择联系人开始新会话"
        variants={presetsMotion.sheet}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.pickerHeader}>
          <h2>新建会话</h2>
          <button type="button" aria-label="关闭" onClick={onClose}>×</button>
        </div>
        {presets.length === 0 ? (
          <div className={styles.pickerEmpty}>
            <p>还没有联系人。</p>
            <button type="button" onClick={() => navigate('/contacts')}>前往联系人</button>
          </div>
        ) : (
          <div className={styles.pickerList}>
            {presets.map((preset) => {
              const avatar = avatarSource(preset.avatar);
              return (
                <button key={preset.id} type="button" className={styles.pickerItem} onClick={() => onChoose(preset)}>
                  <span className={styles.avatar} aria-hidden="true">{avatar ? <img src={avatar} alt="" /> : (firstText(preset.avatar, preset.name.charAt(0), '联'))}</span>
                  <span><strong>{preset.name}</strong><small>{firstText(preset.description, '开始一段新对话')}</small></span>
                </button>
              );
            })}
          </div>
        )}
      </motion.section>
    </>
  );
}

export function MessagesPage(): ReactElement {
  const presetsMotion = useMotionPresets();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [picker, setPicker] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const opening = useRef(false);
  const rootRef = useComponentMeta<HTMLElement>(buildComponentMeta({
    id: 'page.messages.root', page: 'messages', role: 'container', label: '消息页面容器',
    layout: { x: 0, y: PAGE_TOP, width: 390, height: 744 }, zIndex: 2,
  }, 'container'));

  const headerRef = useComponentMeta<HTMLDivElement>(buildComponentMeta({
    id: 'messages.header', page: 'messages', role: 'container', label: '消息页标题栏',
    layout: { x: 0, y: PAGE_TOP, width: 390, height: 58 }, zIndex: 3,
  }, 'container'));

  useEffect(() => {
    const subscription = liveQuery(async () => {
      const [nextConversations, nextPresets, nextMessages] = await Promise.all([
        conversationRepo.listConversations(),
        presetRepo.listPresets(),
        messageRepo.listAllMessages(),
      ]);
      return { nextConversations, nextPresets, nextMessages };
    }).subscribe({
      next: ({ nextConversations, nextPresets, nextMessages }) => {
        setConversations(nextConversations);
        setPresets(nextPresets);
        setMessages(nextMessages);
        setLoaded(true);
      },
      error: () => setError('读取本地消息失败'),
    });
    return () => subscription.unsubscribe();
  }, []);

  const threads = useMemo(() => buildContactThreads(conversations, presets, messages), [conversations, messages, presets]);

  async function openThread(thread: ContactThread): Promise<void> {
    if (opening.current) return;
    opening.current = true;
    try {
      await Promise.all(thread.conversations.map((conversation) => messageRepo.markConversationRead(conversation.id)));
      setActiveConversationId(thread.latestConversation.id);
      navigate('/chat');
    } catch {
      setError('打开会话失败');
    } finally {
      opening.current = false;
    }
  }

  async function createConversation(preset: Preset): Promise<void> {
    try {
      const preference = loadModelPreference();
      const conversation = await conversationRepo.createAndSaveConversation({
        title: preset.name,
        presetId: preset.id,
        settings: loadModelSettings(),
        ...(preference ?? {}),
      });
      setPicker(false);
      setActiveConversationId(conversation.id);
      navigate('/chat');
    } catch {
      setError('新建会话失败');
    }
  }

  return (
    <section ref={rootRef} className={styles.page} data-ui-id="page.messages.root" data-ui-role="container" data-skin-surface="page">
      <div ref={headerRef} className={styles.header} data-ui-id="messages.header" data-ui-role="container">
        <h1>消息</h1>
        <motion.button
          type="button"
          className={styles.newButton}
          aria-label="新建会话"
          whileTap={presetsMotion.tap.whileTap}
          transition={presetsMotion.tap.transition}
          onClick={() => setPicker(true)}
        >
          ＋
        </motion.button>
      </div>
      <motion.ul className={styles.list} aria-label="消息列表" layout>
        <AnimatePresence initial={false}>
          {threads.map((thread) => (
            <ThreadRow key={thread.key} thread={thread} onOpen={(item) => { void openThread(item); }} />
          ))}
        </AnimatePresence>
      </motion.ul>
      {loaded && threads.length === 0 ? (
        <div className={styles.empty}>
          <h2>还没有消息</h2>
          <p>选择一个联系人，开始第一段对话。</p>
          <button type="button" onClick={() => setPicker(true)}>新建会话</button>
        </div>
      ) : null}
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <AnimatePresence>
        {picker ? <ContactPicker presets={presets} onClose={() => setPicker(false)} onChoose={(preset) => { void createConversation(preset); }} /> : null}
      </AnimatePresence>
    </section>
  );
}
